import React from "react";
import { TrendingUp } from "lucide-react";

/**
 * MoonbucksScoreCard — the ONE score subscribers should look at first.
 *
 * A 0-100 headline aggregating pattern strength, smart-money conviction,
 * sentiment, technical conviction, and the market regime — with a clear
 * grade (STRONG BUY / BUY / WATCH / CAUTION / AVOID) and a pillar breakdown
 * so nobody has to guess what drives the number.
 *
 * Backend contract: `data.moonbucks_grade` from `/api/stocks/{symbol}`:
 *   { score: 0-100, label, color, tagline, pillars: [{key,label,value,max,detail}] }
 */
export default function MoonbucksScoreCard({ grade }) {
  if (!grade) return null;
  const { score, label, color, tagline, pillars = [] } = grade;

  const style = GRADE_STYLES[color] || GRADE_STYLES.amber;

  return (
    <div
      className="rounded-xl border p-6 relative overflow-hidden"
      style={{
        background: `linear-gradient(180deg, ${style.tintTop} 0%, ${style.tintBottom} 100%)`,
        borderColor: style.border,
      }}
      data-testid="moonbucks-score-card"
    >
      <div className="flex items-start gap-6 flex-wrap">
        {/* Big score dial */}
        <div className="flex items-center gap-4">
          <div
            className="relative flex items-center justify-center rounded-full"
            style={{
              width: 96, height: 96,
              background: `conic-gradient(${style.ring} ${score * 3.6}deg, rgba(255,255,255,0.08) 0)`,
              boxShadow: `0 0 24px ${style.ring}55`,
            }}
            data-testid="moonbucks-score-dial"
          >
            <div
              className="absolute flex flex-col items-center justify-center rounded-full"
              style={{ inset: 6, background: "#0f0c1d" }}
            >
              <div className="font-mono-data font-extrabold text-3xl leading-none" style={{ color: style.text }}>
                {Math.round(score)}
              </div>
              <div className="text-[9px] text-white/50 mt-0.5 tracking-widest font-mono-data">/100</div>
            </div>
          </div>
          <div>
            <div className="label-uppercase" style={{ color: style.text, letterSpacing: "0.18em" }}>
              Moonbucks Score
            </div>
            <div className="text-[10px] mt-0.5 font-mono-data" style={{ color: "rgba(255,255,255,0.45)" }}>
              // pattern + macro + catalyst risk
            </div>
            <div
              className="font-display font-semibold text-2xl mt-1 flex items-center gap-2"
              style={{ color: style.text }}
              data-testid="moonbucks-grade-label"
            >
              <TrendingUp className="w-5 h-5" strokeWidth={2.5} />
              {label}
            </div>
            <p className="text-sm text-white/70 mt-1 max-w-md">
              {tagline}
            </p>
          </div>
        </div>
      </div>

      {/* Pillar breakdown */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-6" data-testid="moonbucks-pillars">
        {pillars.map((p) => (
          <PillarBar key={p.key} pillar={p} accent={style.ring} />
        ))}
      </div>

      <div className="mt-4 pt-3 border-t text-[10px] text-white/40 leading-relaxed"
           style={{ borderColor: "rgba(255,255,255,0.08)" }}>
        Score is a snapshot of the desk&apos;s checks right now — not a promise. All trading decisions are your own.
      </div>
    </div>
  );
}

function PillarBar({ pillar, accent }) {
  const pct = pillar.max > 0 ? Math.max(0, Math.min(100, (pillar.value / pillar.max) * 100)) : 0;
  return (
    <div
      className="rounded-md p-3"
      style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}
      data-testid={`moonbucks-pillar-${pillar.key}`}
    >
      <div className="flex items-baseline justify-between">
        <span className="text-[10px] font-bold uppercase tracking-widest text-white/60">{pillar.label}</span>
        <span className="font-mono-data text-xs font-bold text-white">
          {Math.round(pillar.value)}<span className="text-white/40 text-[10px]">/{pillar.max}</span>
        </span>
      </div>
      <div className="mt-2 h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.08)" }}>
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${pct}%`, background: accent, boxShadow: `0 0 8px ${accent}88` }}
        />
      </div>
      <div className="text-[10px] text-white/50 mt-1.5 truncate" title={pillar.detail}>{pillar.detail}</div>
    </div>
  );
}

const GRADE_STYLES = {
  emerald: {
    ring: "#38E7A3",
    text: "#B7F7DA",
    border: "rgba(56, 231, 163, 0.4)",
    tintTop: "rgba(56, 231, 163, 0.14)",
    tintBottom: "rgba(56, 231, 163, 0.02)",
  },
  green: {
    ring: "#4ADE80",
    text: "#C4F4D3",
    border: "rgba(74, 222, 128, 0.35)",
    tintTop: "rgba(74, 222, 128, 0.12)",
    tintBottom: "rgba(74, 222, 128, 0.02)",
  },
  amber: {
    ring: "#F5A623",
    text: "#FCE9C3",
    border: "rgba(245, 166, 35, 0.4)",
    tintTop: "rgba(245, 166, 35, 0.12)",
    tintBottom: "rgba(245, 166, 35, 0.02)",
  },
  orange: {
    ring: "#FF8A5B",
    text: "#FED4BF",
    border: "rgba(255, 138, 91, 0.4)",
    tintTop: "rgba(255, 138, 91, 0.12)",
    tintBottom: "rgba(255, 138, 91, 0.02)",
  },
  red: {
    ring: "#FF5C5C",
    text: "#FBC5C5",
    border: "rgba(255, 92, 92, 0.4)",
    tintTop: "rgba(255, 92, 92, 0.14)",
    tintBottom: "rgba(255, 92, 92, 0.02)",
  },
};
